import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';

import {
  FakturowniaInvoicesDocument,
  FakturowniaInvoicesQuery,
  FakturowniaInvoicesQueryVariables,
} from 'core/graphql';

import { IFakturowniaInvoiceListRow, IInvoicesFilters } from './types';
import { isPermissionDeniedError } from './errors';

interface IAllFakturowniaInvoicesHook {
  data: IFakturowniaInvoiceListRow[];
  loading: boolean;
  /** Mirrors `loading` so consumers can dim stale rows during refetch. */
  fetching: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  refetch: () => void;
}

/** Page size for each batch — `perPage` over the wire. Big enough to keep
 *  the request count low, small enough to start rendering quickly. */
const BATCH_SIZE = 200;

/**
 * Auto-paginating list hook. Walks every page of `fakturowniaInvoices` for
 * the given filter until the backend returns a short page, then settles.
 * Rows accumulate into `data` progressively so the UI can render the first
 * batch while later pages are still in flight. Phase 9.1: replaces every
 * call site that used the page-capped `useFakturowniaInvoices` for a
 * «всё в выборке» semantic — InvoicesSummary, OverdueList, student/group
 * detail tabs etc. now get the complete set, not an arbitrary first slice.
 */
export const useAllFakturowniaInvoices = (
  filters: IInvoicesFilters,
): IAllFakturowniaInvoicesHook => {
  const client = useApolloClient();
  const [rows, setRows] = useState<IFakturowniaInvoiceListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApolloError | undefined>(undefined);
  const [refetchTick, setRefetchTick] = useState(0);

  // Strip undefined/empty values so the variables object stays stable and
  // we don't blow the apollo cache key with `key: undefined` noise.
  const baseVariables: Omit<
    FakturowniaInvoicesQueryVariables,
    'page' | 'perPage'
  > = useMemo(
    () => ({
      direction: filters.direction || undefined,
      status: filters.status || undefined,
      matchStatus: filters.matchStatus || undefined,
      kind: filters.kind || undefined,
      studentId: filters.studentId || undefined,
      groupId: filters.groupId || undefined,
      agreementId: filters.agreementId || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      search: filters.search || undefined,
      overdueOnly: filters.overdueOnly || undefined,
      parentId: filters.parentId || undefined,
      needsLinkOnly: filters.needsLinkOnly || undefined,
      provisionBlockedOnly: filters.provisionBlockedOnly || undefined,
    }),
    [
      filters.direction,
      filters.status,
      filters.matchStatus,
      filters.kind,
      filters.studentId,
      filters.groupId,
      filters.agreementId,
      filters.dateFrom,
      filters.dateTo,
      filters.search,
      filters.overdueOnly,
      filters.parentId,
      filters.needsLinkOnly,
      filters.provisionBlockedOnly,
    ],
  );

  // Stable string key — JSON.stringify is cheap on this tiny object and
  // lets us depend on «filter values» rather than the object identity.
  const variablesKey = JSON.stringify(baseVariables);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    // Phase 11: stale-while-revalidate — DON'T blank `rows` here. The page
    // dims the previous list via `S.DimWhenFetching` while the next set
    // streams in, and the page-level full-screen preloader only fires when
    // `rows.length === 0` (true first mount). Wiping rows on every filter
    // change made every chip click feel like a hard navigation.

    const fetchAll = async () => {
      const accumulator: IFakturowniaInvoiceListRow[] = [];
      let page = 1;
      try {
        // Loop until we hit a short page — that's the «no more data» signal
        // the rest of the codebase already uses (TZ §5 — pagination heuristic).
        // Sequential by design: we only know if a next page exists after
        // the current one returns. `no-await-in-loop` doesn't apply.
        // eslint-disable-next-line no-constant-condition, no-await-in-loop
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const result = await client.query<
            FakturowniaInvoicesQuery,
            FakturowniaInvoicesQueryVariables
          >({
            query: FakturowniaInvoicesDocument,
            variables: { ...baseVariables, page, perPage: BATCH_SIZE },
            fetchPolicy: 'network-only',
          });
          if (cancelled) return;

          const batch = (
            result.data?.fakturowniaInvoices || []
          ).filter((r): r is IFakturowniaInvoiceListRow => Boolean(r));
          accumulator.push(...batch);
          // Progressive update — first 200 visible while page 2/3/… load.
          setRows(accumulator.slice());

          if (batch.length < BATCH_SIZE) break;
          page += 1;
        }
      } catch (e) {
        if (!cancelled) setError(e as ApolloError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
    // We depend on variablesKey (the serialized base variables), the
    // refetchTick (manual re-trigger) and the client identity. Lint can't
    // see through JSON.stringify, so we acknowledge the closure-over-
    // baseVariables explicitly via the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variablesKey, refetchTick, client]);

  const refetch = useCallback(() => setRefetchTick((t) => t + 1), []);

  return {
    data: rows,
    loading,
    fetching: loading,
    error,
    permissionDenied: isPermissionDeniedError(error),
    refetch,
  };
};
